import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";
import {
  formatApiPaymentDisplayLabel,
  isUrdLinkedPaymentMode,
  parsePaymentModeFromApi,
  PAYMENT_METHOD_LABELS,
} from "../utils/paymentLinesErpAdapter";
import { getErpBranchDetails, getTenantBrandImageSrcWithFallback } from "../erpClient";
import { getSessionBranchId } from "../utils/sessionBranch";
import { capitalizeWords } from "../utils/capitalizeWords";
import { getTenantNameUpper } from "../utils/tenantName";
import { computeBillTaxRoundOff } from "../utils/autoRoundOff";
import { getInvoiceCashReturnAmount } from "../utils/invoiceCashReturn";

const SECTION_GAP = 3;
const LINE_COLOR = "#7d0000";
const BORDER_LINE = `1pt solid ${LINE_COLOR}`;
// A5 dimensions and margins
const A5_WIDTH = 419.53;
const A5_HEIGHT = 595.28;
const PAGE_PAD_TOP = 14;
const PAGE_PAD_BOTTOM = 14;
const PAGE_PAD_HORIZONTAL = 16;

Font.register({
  family: "NotoSansGujaratiUI",
  src: "/assets/fonts/gujarati/NotoSansGujaratiUI-Regular.ttf",
});

Font.register({
  family: "NotoSansGujaratiUI",
  src: "/assets/fonts/gujarati/NotoSansGujaratiUI-Bold.ttf",
  fontWeight: "bold",
});

Font.register({
  family: "Times New Roman",
  src: "https://db.onlinewebfonts.com/t/32441506567156636049eb850b53f02a.ttf",
});

function formatBranchAddressLines(address) {
  const raw = String(address || "").trim();
  if (!raw) return ["-"];
  if (raw.includes("\n")) {
    return raw
      .split(/\n/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  const parts = raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length <= 1) return [raw];
  if (parts.length === 2) return [`${parts[0]},`, parts[1]];
  return [`${parts.slice(0, 2).join(", ")},`, parts.slice(2).join(", ")];
}

const A5_CONTENT_WIDTH = A5_WIDTH - PAGE_PAD_HORIZONTAL * 2;
// Logo + mobile columns reserve side space inside company header.
const A5_COMPANY_TITLE_MAX_WIDTH = A5_CONTENT_WIDTH - 16 - 140;

/** Shrink company title so long names stay on one line in the A5 header. */
function getA5CompanyTitleStyle(name) {
  const text = String(name || "").trim();
  const baseFontSize = 22;
  const minFontSize = 8;
  const maxWidth = A5_COMPANY_TITLE_MAX_WIDTH * 0.92;

  if (!text) {
    return {
      fontSize: baseFontSize,
      letterSpacing: 0.4,
      width: A5_COMPANY_TITLE_MAX_WIDTH,
    };
  }

  const estimateWidth = (fontSize, letterSpacing) => {
    let width = 0;
    for (let i = 0; i < text.length; i += 1) {
      const ch = text[i];
      if (ch === " ") width += fontSize * 0.38;
      else if (ch === "I" || ch === "J" || ch === "L") width += fontSize * 0.42;
      else if (ch === "M" || ch === "W") width += fontSize * 0.88;
      else width += fontSize * 0.74;
      if (i < text.length - 1) width += letterSpacing;
    }
    return width;
  };

  let fontSize = baseFontSize;
  let letterSpacing = 0.4;

  while (fontSize > minFontSize) {
    letterSpacing =
      fontSize <= 11 ? 0.05 : fontSize <= 14 ? 0.15 : fontSize <= 18 ? 0.25 : 0.4;
    if (estimateWidth(fontSize, letterSpacing) <= maxWidth) break;
    fontSize -= 0.25;
  }

  return {
    fontSize: Math.max(minFontSize, fontSize),
    letterSpacing,
    width: A5_COMPANY_TITLE_MAX_WIDTH,
  };
}

/** Scale rate font size so "+ L.C." always stays on a single line in A5 column width. */
function getA5RateStyle(rateText) {
  const len = String(rateText || "").length;
  let fontSize = 5.5;
  if (len >= 14) fontSize = 4.6;
  else if (len >= 12) fontSize = 5.0;
  return {
    fontSize,
    lineHeight: 1.1,
    textAlign: "center",
  };
}

const roundedBox = {
  border: BORDER_LINE,
  borderRadius: 8,
  overflow: "hidden",
};

const styles = StyleSheet.create({
  page: {
    paddingTop: PAGE_PAD_TOP,
    paddingBottom: PAGE_PAD_BOTTOM,
    paddingHorizontal: PAGE_PAD_HORIZONTAL,
    fontFamily: "Times-Roman",
    backgroundColor: "#fff",
    flexDirection: "column",
  },
  container: {
    flexDirection: "column",
  },
  topHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 2,
    paddingHorizontal: 10,
    borderBottom: BORDER_LINE,
  },
  topHeaderText: {
    fontSize: 7,
    fontWeight: "bold",
    fontFamily: "NotoSansGujaratiUI",
    color: LINE_COLOR,
  },
  letterheadBox: {
    border: BORDER_LINE,
    borderRadius: 6,
    overflow: "hidden",
    marginBottom: 6,
    flexShrink: 0,
  },
  companyHeader: {
    position: "relative",
    paddingTop: 4,
    paddingBottom: 5,
    paddingHorizontal: 8,
    minHeight: 44,
  },
  logoColumn: {
    position: "absolute",
    left: 8,
    top: 6,
    width: 28,
    height: 28,
    zIndex: 2,
  },
  logo: {
    width: 26,
    height: 26,
  },
  centerBrandBlock: {
    width: "100%",
    alignItems: "center",
    paddingHorizontal: 70,
  },
  companyTitle: {
    fontFamily: "Times-Bold",
    fontSize: 22,
    color: "#7d0000",
    letterSpacing: 0.4,
    textAlign: "center",
    width: "100%",
  },
  companyAddress: {
    fontSize: 6.5,
    color: "#000",
    textAlign: "center",
    marginTop: 2,
    width: "100%",
  },
  mobileColumn: {
    position: "absolute",
    right: 8,
    top: 6,
    zIndex: 2,
  },
  companyMobile: {
    fontSize: 7,
    fontWeight: "bold",
    fontFamily: "NotoSansGujaratiUI",
    color: "#000",
    textAlign: "right",
  },
  invoiceHeader: {
    ...roundedBox,
    marginBottom: SECTION_GAP,
    flexShrink: 0,
  },
  mainContainer: {
    flexDirection: "row",
  },
  leftSection: {
    width: "58%",
    flexDirection: "column",
  },
  rightSection: {
    width: "42%",
    borderLeft: BORDER_LINE,
    flexDirection: "column",
  },
  topSection: {
    borderBottom: BORDER_LINE,
    padding: "2 4",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 16,
  },
  bottomSection: {
    padding: "2 4",
  },
  taxInvoice: {
    fontSize: 7,
    fontFamily: "Times-Bold",
    fontWeight: "bold",
    textAlign: "center",
  },
  original: {
    fontSize: 7,
    fontFamily: "Times-Bold",
    fontWeight: "bold",
    textAlign: "center",
  },
  stateText: {
    fontSize: 7,
    fontWeight: "bold",
    marginBottom: 1,
  },
  rightTopSection: {
    borderBottom: BORDER_LINE,
    padding: "2 4",
    minHeight: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  customerSection: {
    ...roundedBox,
    marginBottom: SECTION_GAP,
    flexShrink: 0,
  },
  customerHeader: {
    borderBottom: BORDER_LINE,
    padding: "1 2",
    alignItems: "center",
    justifyContent: "center",
  },
  customerHeaderText: {
    fontSize: 6.5,
    fontWeight: "bold",
    textAlign: "center",
  },
  customerContent: {
    flexDirection: "row",
  },
  customerLeft: {
    width: "58%",
    borderRight: BORDER_LINE,
    // Reduce left padding so labels sit closer to the left border
    paddingTop: 2,
    paddingBottom: 2,
    paddingLeft: 3,
    paddingRight: 4,
    overflow: "hidden",
  },
  customerRight: {
    width: "42%",
    paddingTop: 2,
    paddingBottom: 2,
    paddingLeft: 3,
    paddingRight: 4,
    overflow: "hidden",
  },
  detailRow: {
    flexDirection: "row",
    marginBottom: 1,
    alignItems: "flex-start",
    width: "100%",
  },
  detailLabel: {
    fontSize: 7,
    // Tighter column so values sit closer to labels (was 52)
    width: 42,
    fontWeight: "bold",
    flexShrink: 0,
  },
  detailLabelRight: {
    fontSize: 7,
    // Fits "ADHAR NO" while pulling values left (was 52)
    width: 44,
    fontWeight: "bold",
    flexShrink: 0,
  },
  detailColon: {
    fontSize: 7,
    fontWeight: "bold",
    flexShrink: 0,
    width: 6,
  },
  detailValueWrap: {
    flex: 1,
    paddingRight: 2,
  },
  detailValue: {
    fontSize: 7,
    fontWeight: "bold",
  },
  tableFooterBox: {
    ...roundedBox,
    flexDirection: "column",
  },
  tableHeader: {
    flexDirection: "row",
    borderBottom: BORDER_LINE,
    fontSize: 7,
    fontWeight: "bold",
    minHeight: 20,
    flexShrink: 0,
  },
  tableRow: {
    flexDirection: "row",
    alignItems: "stretch",
    minHeight: 26,
    fontSize: 7,
    flexShrink: 0,
  },
  emptyRow: {
    flexDirection: "row",
    alignItems: "stretch",
  },
  fillerCol: {
    alignSelf: "stretch",
  },
  tableCol1: {
    width: "4%",
    borderRight: BORDER_LINE,
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    // Top-align with item name / other values (match A4)
    justifyContent: "flex-start",
    padding: 2,
    paddingTop: 2,
  },
  tableCol2: {
    width: "40%",
    borderRight: BORDER_LINE,
    position: "relative",
    padding: 2,
    paddingTop: 2,
    alignSelf: "stretch",
    justifyContent: "flex-start",
  },
  purityText: {
    position: "absolute",
    right: 4,
    top: 2,
    fontSize: 7,
    fontWeight: "bold",
  },
  tableCol4: {
    width: "8%",
    borderRight: BORDER_LINE,
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: 2,
    paddingTop: 2,
  },
  tableCol5: {
    width: "10%",
    borderRight: BORDER_LINE,
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: 2,
    paddingTop: 2,
  },
  tableCol6: {
    width: "10%",
    borderRight: BORDER_LINE,
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: 2,
    paddingTop: 2,
  },
  tableCol7: {
    width: "10%",
    borderRight: BORDER_LINE,
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: 2,
    paddingHorizontal: 1,
    paddingTop: 2,
  },
  tableCol8: {
    width: "8%",
    borderRight: BORDER_LINE,
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: 2,
    paddingTop: 2,
  },
  tableCol9: {
    width: "10%",
    textAlign: "right",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "flex-start",
    padding: "2 4",
    paddingTop: 2,
  },
  tableHeaderText: {
    textAlign: "left",
    fontWeight: "bold",
    fontSize: 7,
  },
  itemNameContainer: {
    flex: 1,
    paddingRight: 20,
    // No extra top padding — tableCol2 already has padding so it lines up with No.
    paddingTop: 0,
    paddingBottom: 0,
    justifyContent: "flex-start",
  },
  itemName: {
    fontSize: 7,
    fontWeight: "bold",
    maxWidth: "85%",
    lineHeight: 1.2,
    textAlign: "left",
  },
  chargeDetails: {
    // Smaller than item name so charge labels (HALLMARK, Diamond, etc.) read as secondary
    fontSize: 6,
    fontWeight: "bold",
    color: "#000000",
    marginTop: 1,
    marginBottom: 1,
    maxWidth: "85%",
  },
  cellText: {
    fontSize: 7,
    textAlign: "center",
  },
  rateCellText: {
    fontSize: 5.5,
    lineHeight: 1.1,
    textAlign: "center",
  },
  amountText: {
    fontSize: 7,
    textAlign: "right",
  },
  tableHeaderCol2: {
    width: "40%",
    borderRight: BORDER_LINE,
    position: "relative",
    padding: 2,
    paddingTop: 2,
    minHeight: 20,
    // Top-align with "No." (was center → ITEM NAME looked lower)
    justifyContent: "flex-start",
  },
  totalRow: {
    flexDirection: "row",
    alignItems: "stretch",
    borderBottom: BORDER_LINE,
    overflow: "hidden",
    fontSize: 7,
  },
  footerSummaryBody: {
    position: "relative",
    overflow: "hidden",
  },
  footerVLine: {
    position: "absolute",
    left: "50%",
    top: 0,
    bottom: 0,
    width: 0,
    borderRight: BORDER_LINE,
    zIndex: 2,
  },
  summaryGrid: {
    flexDirection: "column",
    overflow: "hidden",
  },
  summaryRow1: {
    flexDirection: "row",
    alignItems: "stretch",
    borderBottom: BORDER_LINE,
  },
  summaryRowBody: {
    flexDirection: "row",
    alignItems: "stretch",
  },
  summaryColLeft: {
    width: "50%",
    flexDirection: "column",
    overflow: "hidden",
  },
  summaryColRight: {
    width: "50%",
    flexDirection: "column",
    overflow: "hidden",
  },
  summaryTotalLeft: {
    width: "50%",
    flexDirection: "row",
    alignItems: "center",
    padding: "2 4",
    minHeight: 18,
  },
  summaryTotalRight: {
    width: "50%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "2 4",
    minHeight: 18,
  },
  noteLabel: {
    fontWeight: "bold",
    marginRight: 4,
  },
  noteValue: {
    flex: 1,
  },
  amountWordsCell: {
    width: "50%",
    padding: "2 4",
    fontSize: 7,
    fontStyle: "italic",
    justifyContent: "center",
  },
  taxRowCell: {
    width: "50%",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "2 4",
    paddingLeft: 6,
    paddingRight: 4,
  },
  paymentSplitRow: {
    flexDirection: "row",
    flex: 1,
    alignItems: "stretch",
    marginLeft: -4,
    marginRight: -4,
    position: "relative",
    overflow: "hidden",
  },
  paymentVLine: {
    position: "absolute",
    left: "50%",
    top: 0,
    bottom: 0,
    width: 0,
    borderRight: BORDER_LINE,
    zIndex: 1,
  },
  paymentSplitLeft: {
    width: "50%",
    flexDirection: "column",
  },
  paymentSplitRight: {
    width: "50%",
    flexDirection: "column",
  },
  paymentPad: {
    paddingTop: 2,
    paddingBottom: 2,
    paddingRight: 4,
    paddingLeft: 8,
  },
  totalAmountLabel: {
    fontSize: 7,
    fontWeight: "bold",
  },
  totalAmountValue: {
    fontSize: 7,
    fontWeight: "bold",
    textAlign: "right",
  },
  taxRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottom: BORDER_LINE,
    padding: "2 4",
    paddingLeft: 6,
    paddingRight: 4,
    marginLeft: -2,
    marginRight: -2,
  },
  taxLabel: {
    fontSize: 7,
    fontWeight: "bold",
  },
  taxValue: {
    fontSize: 7,
    fontWeight: "bold",
    textAlign: "right",
  },
  paymentMethod: {
    flexDirection: "row",
    fontSize: 7,
    marginBottom: 1,
  },
  paymentMethodLabel: {
    fontWeight: "bold",
    fontSize: 7,
  },
  authSection: {
    borderTop: BORDER_LINE,
    paddingTop: 4,
    paddingBottom: 6,
    paddingHorizontal: 2,
    marginTop: 0,
    flexShrink: 0,
  },
  footerTopRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 1,
    marginTop: 0,
  },
  jurisdictionText: {
    fontSize: 7,
    fontWeight: "bold",
  },
  certificationText: {
    fontSize: 7,
    fontStyle: "italic",
    lineHeight: 1.1,
    textAlign: "right",
  },
  footerTopRight: {
    alignItems: "flex-end",
    paddingRight: 12,
  },
  companySignature: {
    fontSize: 7,
    fontWeight: "bold",
    marginTop: 0,
    marginBottom: 1,
  },
  signatureSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    position: "relative",
    marginTop: 0,
    paddingBottom: 4,
    minHeight: 44,
    flexShrink: 0,
  },
  signatureColumnLeft: {
    width: "30%",
    alignItems: "flex-start",
    justifyContent: "flex-end",
    marginTop: 12,
  },
  signatureColumnCenter: {
    width: "30%",
  },
  signatureColumnRight: {
    width: "30%",
    alignItems: "flex-end",
    flexDirection: "column",
    justifyContent: "space-between",
    alignSelf: "stretch",
    marginTop: 0,
    paddingRight: 12,
  },
  signatureColumnRightTop: {
    alignItems: "flex-end",
    justifyContent: "flex-start",
  },
  signatureText: {
    fontSize: 7,
    fontWeight: "bold",
  },
  signatureImage: {
    width: 72,
    height: 26,
    objectFit: "contain",
    flexShrink: 0,
  },
});

const InvoicePDFA5 = ({ invoiceData }) => {
  const data = invoiceData || {};
  const customer = data.customer || {};
  const invoiceItems = Array.isArray(data.items) ? data.items : [];

  const sessionBranchId = getSessionBranchId();
  const branchList = getErpBranchDetails();
  const branchDetails =
    branchList.find((b) => Number(b?.id) === Number(sessionBranchId)) ||
    branchList[0] ||
    {};
  const companyMobile = branchDetails.mobile || branchDetails.phone || "-";
  const companyNameUpper = getTenantNameUpper();
  const companyTitleStyle = getA5CompanyTitleStyle(companyNameUpper);
  const companyGstin =
    branchDetails.gst_number ||
    branchDetails.gstin ||
    branchDetails.GST_number ||
    "-";
  const addressLines = formatBranchAddressLines(branchDetails.address);
  const addressSingleLine =
    String(branchDetails.address || "")
      .replace(/\s*\n\s*/g, ", ")
      .replace(/,\s*,/g, ",")
      .replace(/\s+/g, " ")
      .trim() ||
    addressLines.join(", ").replace(/\s+/g, " ").trim() ||
    "-";

  const gstPercent = parseFloat(data.gst_percent || 0) || 0;
  const baseSgst = parseFloat(data.sgst || 0) || 0;
  const baseCgst = parseFloat(data.cgst || 0) || 0;
  const baseIgst = parseFloat(data.igst || 0) || 0;
  const baseGstAmt =
    parseFloat(data.gst_amount ?? data.tax_amount ?? data.totalTax ?? 0) || 0;
  const customerState = String(customer.state || "")
    .trim()
    .toLowerCase();
  const isGujarat =
    customerState === "" ||
    customerState.includes("gujarat") ||
    customerState === "gj";
  const noTaxAmounts =
    baseSgst + baseCgst + baseIgst <= 0 && baseGstAmt > 0 && gstPercent > 0;
  const sgstAmount = noTaxAmounts && isGujarat ? baseGstAmt / 2 : baseSgst;
  const cgstAmount = noTaxAmounts && isGujarat ? baseGstAmt / 2 : baseCgst;
  const igstAmount = noTaxAmounts && !isGujarat ? baseGstAmt : baseIgst;
  const totalBeforeTaxForRound =
    parseFloat(data.final_amount || data.subtotal || 0) || 0;
  const discountInputA5 = parseFloat(data.discount) || 0;
  const discountAmountRawA5 = parseFloat(data.discount_amount);
  const discountAmountForRound =
    Number.isFinite(discountAmountRawA5) && discountAmountRawA5 > 0
      ? discountAmountRawA5
      : discountInputA5 > 0 &&
          discountInputA5 <= 100 &&
          totalBeforeTaxForRound > 0
        ? (totalBeforeTaxForRound * discountInputA5) / 100
        : discountInputA5 > 0
          ? discountInputA5
          : 0;
  const paidForRound = parseFloat(data.paidAmount ?? data.paid_amount) || 0;
  const remainingForRound =
    parseFloat(data.remainingAmount ?? data.remaining_amount) || 0;
  const preferredBillTotal =
    remainingForRound <= 0.009 && paidForRound > 0
      ? paidForRound
      : parseFloat(data.grandTotal ?? data.grand_total) || undefined;
  const {
    displaySgst: displaySgstAmount,
    displayCgst: displayCgstAmount,
    displayIgst: displayIgstAmount,
    roundOff: autoRoundOffAmount,
    amountAfterTax,
    roundOffLabel,
  } = computeBillTaxRoundOff({
    totalBeforeTax: totalBeforeTaxForRound,
    discountAmount: discountAmountForRound,
    sgst: sgstAmount,
    cgst: cgstAmount,
    igst: igstAmount,
    preferredTotal: preferredBillTotal,
  });

  const getPaymentLabel = (payment) => formatApiPaymentDisplayLabel(payment);
  const getPaymentAmount = (payment) => parseFloat(payment?.amount ?? 0) || 0;
  const isOldMetalPayment = (payment) =>
    isUrdLinkedPaymentMode(payment?.payment_mode) ||
    Number(payment?.urd_document_id) > 0;
  const paymentList = (Array.isArray(data.payments) ? data.payments : []).filter(
    (p) => getPaymentAmount(p) > 0 || isOldMetalPayment(p),
  );
  const oldMetalPayments = paymentList.filter((p) => isOldMetalPayment(p));
  const leftColumnPayments = [
    ...oldMetalPayments,
    ...paymentList.filter((p) => !isOldMetalPayment(p)),
  ];
  const cashReturnAmount = getInvoiceCashReturnAmount(data);
  const getOldMetalPaymentLabel = (payment) => {
    const payMode = parsePaymentModeFromApi(payment?.payment_mode);
    return PAYMENT_METHOD_LABELS[payMode] || "Old Gold";
  };

  const getDisplayItemName = (item = {}) => {
    const rawName =
      item?.name ??
      item?.item_category_name ??
      item?.product_group_name ??
      item?.design ??
      item?.tag_no ??
      "";
    const safeName =
      rawName === null || rawName === undefined ? "" : String(rawName);
    return safeName.includes("-")
      ? safeName.split("-").slice(1).join("-").trim()
      : safeName;
  };

  const getChargeTypeLabel = (charge = {}) => {
    const raw = charge.charge_type || charge.chargeType || charge.type || "";
    return String(raw)
      .replace(/(charge|charges)/gi, "")
      .replace(/\s+s\b/g, "")
      .trim();
  };

  const estimateItemNameLines = (item = {}) => {
    const name = getDisplayItemName(item);
    if (!name) return 1;
    // A5 ITEM NAME column is narrower than A4
    const charsPerLine = 22;
    const lines = String(name)
      .split(/\r?\n/)
      .reduce(
        (sum, part) => sum + Math.max(1, Math.ceil(part.length / charsPerLine)),
        0,
      );
    return Math.max(1, lines);
  };

  const getItemRowMinHeight = (item = {}) => {
    const chargeCount = Array.isArray(item.charges) ? item.charges.length : 0;
    const nameLines = estimateItemNameLines(item);
    const hasIssue = Boolean(item.issueDescription);
    const hasHuid = Boolean(item.huid && String(item.huid).trim());

    const computed =
      6 +
      nameLines * 9 +
      (hasIssue ? 8 : 0) +
      chargeCount * 9 +
      (hasHuid ? 9 : 0);

    return Math.max(22, computed);
  };

  /** Vertical offset for charge detail lines under the main item values */
  const getChargeLineTop = (item, chargeIndex) => {
    const nameLines = estimateItemNameLines(item);
    const itemNameHeight = nameLines * 9;
    const issueDescriptionHeight = item.issueDescription ? 8 : 0;
    const baseTop = 2 + itemNameHeight + issueDescriptionHeight;
    return baseTop + chargeIndex * 9;
  };

  const totalBeforeTax = parseFloat(data.final_amount || data.subtotal || 0);
  const MAX_PAGE_ITEMS = 4;
  const pageBodyWithFooter = 210;

  const invoiceItemsWithSerial = invoiceItems.map((item, index) => ({
    ...item,
    __globalSerial: index + 1,
  }));

  const fillPageFromQueue = (queue, limit, maxItems = MAX_PAGE_ITEMS) => {
    const page = [];
    let height = 0;
    while (queue.length > 0 && page.length < maxItems) {
      const rowHeight = getItemRowMinHeight(queue[0]);
      if (page.length > 0 && height + rowHeight > limit) break;
      if (page.length === 0 && rowHeight > limit) {
        page.push(queue.shift());
        break;
      }
      page.push(queue.shift());
      height += rowHeight;
    }
    return page;
  };

  const splitItemsIntoPages = (items = []) => {
    if (!Array.isArray(items) || items.length === 0) return [[]];

    if (items.length <= MAX_PAGE_ITEMS) {
      const totalHeight = items.reduce(
        (sum, item) => sum + getItemRowMinHeight(item),
        0,
      );
      if (totalHeight <= pageBodyWithFooter) {
        return [items];
      }
    }

    const queue = [...items];
    const pages = [
      fillPageFromQueue(queue, pageBodyWithFooter, MAX_PAGE_ITEMS),
    ];

    while (queue.length > 0) {
      pages.push(
        fillPageFromQueue(queue, pageBodyWithFooter, MAX_PAGE_ITEMS),
      );
    }

    return pages;
  };

  const pageItemGroups = splitItemsIntoPages(invoiceItemsWithSerial);
  const totalPages = pageItemGroups.length;

  return (
    <Document>
      {pageItemGroups.map((pageItems, pageIndex) => {
        const isFirstPage = pageIndex === 0;
        const occupiedTableBodyHeight = pageItems.reduce(
          (sum, item) => sum + getItemRowMinHeight(item),
          0,
        );
        const trailingBlankRowHeight = Math.max(
          0,
          pageBodyWithFooter - occupiedTableBodyHeight,
        );

        return (
          <Page
            key={`invoice-a5-page-${pageIndex}`}
            size="A5"
            style={styles.page}
            wrap={false}
          >
            <View style={styles.container}>
              <View style={styles.letterheadBox}>
                <View style={styles.topHeader}>
                  <Text style={styles.topHeaderText}>
                    ॥ શ્રી પદમાવતી માતાય નમઃ ॥
                  </Text>
                  <Text style={styles.topHeaderText}>॥ શ્રી ગણેશાય નમઃ ॥</Text>
                  <Text style={styles.topHeaderText}>
                    ॥ શ્રી બુટભવાની માતાય નમઃ ॥
                  </Text>
                </View>

                <View style={styles.companyHeader}>
                  <View style={styles.mobileColumn}>
                    <Text style={styles.companyMobile} wrap={false}>
                      MO.: {companyMobile}
                    </Text>
                  </View>
                  <View style={styles.logoColumn}>
                    <Image
                      src={getTenantBrandImageSrcWithFallback("sidebar_icon")}
                      style={styles.logo}
                    />
                  </View>
                  <View style={styles.centerBrandBlock}>
                    <Text
                      style={[styles.companyTitle, companyTitleStyle]}
                      wrap={false}
                      hyphenationCallback={(word) => [word]} // keep name on one line
                    >
                      {companyNameUpper}
                    </Text>
                    <Text style={styles.companyAddress} wrap={false}>
                      {addressSingleLine}
                    </Text>
                  </View>
                </View>
              </View>

              <View style={styles.invoiceHeader}>
                <View style={styles.mainContainer}>
                  <View style={styles.leftSection}>
                    <View style={styles.topSection}>
                      <Text style={styles.taxInvoice} wrap={false}>
                        {isFirstPage ? "TAX INVOICE" : "TAX INVOICE"}
                      </Text>
                    </View>
                    <View style={styles.bottomSection}>
                      <Text style={styles.stateText}>
                        GST- {companyGstin || "-"}
                      </Text>
                      <Text style={styles.stateText}>
                        STATE NAME & CODE - GUJARAT(24)
                      </Text>
                    </View>
                  </View>
                  <View style={styles.rightSection}>
                    <View style={styles.rightTopSection}>
                      <Text style={styles.original} wrap={false}>
                        {totalPages > 1 ? `ORIGINAL` : "ORIGINAL"}
                      </Text>
                    </View>
                    <View style={styles.bottomSection}>
                      <Text style={styles.stateText}>
                        INVOICE DATE : {data.date}
                      </Text>
                      <Text style={styles.stateText}>
                        INVOICE NO : {data.invoiceNumber}
                      </Text>
                    </View>
                  </View>
                </View>
              </View>

              <View style={styles.customerSection}>
                <View style={styles.customerHeader}>
                  <Text style={styles.customerHeaderText}>
                    Details of Receiver | Billed to:
                  </Text>
                </View>
                <View style={styles.customerContent}>
                  <View style={styles.customerLeft}>
                    <View style={styles.detailRow}>
                      <Text style={styles.detailLabel}>NAME</Text>
                      <Text style={styles.detailColon}>:</Text>
                      <View style={styles.detailValueWrap}>
                        <Text style={styles.detailValue}>
                          {capitalizeWords(data.billed_to_name || customer.name) || "-"}
                        </Text>
                      </View>
                    </View>
                    <View style={styles.detailRow}>
                      <Text style={styles.detailLabel}>ADDRESS</Text>
                      <Text style={styles.detailColon}>:</Text>
                      <View style={styles.detailValueWrap}>
                        <Text style={styles.detailValue}>
                          {(() => {
                            const addr = (data.billed_to_address || "").trim();
                            const area = (data.billed_to_area || "").trim();
                            const fullBilledAddr =
                              addr && area
                                ? (addr.toLowerCase().includes(area.toLowerCase())
                                    ? addr
                                    : `${addr}, ${area}`)
                                : (addr || area);
                            return fullBilledAddr || customer.address || "-";
                          })()}
                        </Text>
                      </View>
                    </View>
                    <View style={styles.detailRow}>
                      <Text style={styles.detailLabel}>CITY</Text>
                      <Text style={styles.detailColon}>:</Text>
                      <View style={styles.detailValueWrap}>
                        <Text style={styles.detailValue}>
                          {data.billed_to_city || customer.city || "-"}
                        </Text>
                      </View>
                    </View>
                    <View style={styles.detailRow}>
                      <Text style={styles.detailLabel}>STATE</Text>
                      <Text style={styles.detailColon}>:</Text>
                      <View style={styles.detailValueWrap}>
                        <Text style={styles.detailValue}>
                          {(data.billed_to_state || customer.state) === "0"
                            ? "24-Gujarat"
                            : data.billed_to_state || customer.state || ""}
                        </Text>
                      </View>
                    </View>
                  </View>
                  <View style={styles.customerRight}>
                    <View style={styles.detailRow}>
                      <Text style={styles.detailLabelRight}>MOBILE</Text>
                      <Text style={styles.detailColon}>:</Text>
                      <View style={styles.detailValueWrap}>
                        <Text style={styles.detailValue}>
                          {data.billed_to_mobile || customer.mobile || "-"}
                        </Text>
                      </View>
                    </View>
                    <View style={styles.detailRow}>
                      <Text style={styles.detailLabelRight}>GSTIN</Text>
                      <Text style={styles.detailColon}>:</Text>
                      <View style={styles.detailValueWrap}>
                        <Text style={styles.detailValue}>
                          {customer.GST_number || "-"}
                        </Text>
                      </View>
                    </View>
                    <View style={styles.detailRow}>
                      <Text style={styles.detailLabelRight}>PAN</Text>
                      <Text style={styles.detailColon}>:</Text>
                      <View style={styles.detailValueWrap}>
                        <Text style={styles.detailValue}>
                          {data.billed_to_pan_no || customer.pan_no || "-"}
                        </Text>
                      </View>
                    </View>
                    <View style={styles.detailRow}>
                      <Text style={styles.detailLabelRight}>ADHAR NO</Text>
                      <Text style={styles.detailColon}>:</Text>
                      <View style={styles.detailValueWrap}>
                        <Text style={styles.detailValue}>
                          {data.billed_to_aadhar_no || customer.aadhar_no || "-"}
                        </Text>
                      </View>
                    </View>
                  </View>
                </View>
              </View>

              <View style={styles.tableFooterBox}>
                <View style={[styles.tableHeader, { borderTop: 0 }]}>
                  <Text style={styles.tableCol1}>No.</Text>
                  <View style={styles.tableHeaderCol2}>
                    <Text style={[styles.tableHeaderText, { marginRight: 20 }]}>
                      ITEM NAME
                    </Text>
                    <Text style={styles.purityText}>Ct.</Text>
                  </View>
                  <Text
                    style={[styles.tableCol4, { justifyContent: "flex-start" }]}
                  >
                    HSN
                  </Text>
                  <Text
                    style={[styles.tableCol5, { justifyContent: "flex-start" }]}
                  >
                    GR.WT
                  </Text>
                  <Text
                    style={[styles.tableCol6, { justifyContent: "flex-start" }]}
                  >
                    NET WT.
                  </Text>
                  <Text
                    style={[styles.tableCol7, { justifyContent: "flex-start" }]}
                  >
                    RATE
                  </Text>
                  <Text
                    style={[styles.tableCol8, { justifyContent: "flex-start" }]}
                  >
                    OTHER
                  </Text>
                  <Text
                    style={[styles.tableCol9, { justifyContent: "flex-start" }]}
                  >
                    AMOUNT
                  </Text>
                </View>

                {pageItems.map((item, index) => (
                  <View
                    key={`item-${pageIndex}-${item.__globalSerial}`}
                    style={[
                      styles.tableRow,
                      {
                        // Match A4 invoice: horizontal rule between line items
                        borderBottom:
                          index < pageItems.length - 1 ? BORDER_LINE : 0,
                        minHeight: getItemRowMinHeight(item),
                      },
                    ]}
                  >
                    <Text style={styles.tableCol1}>{item.__globalSerial}</Text>
                    <View style={styles.tableCol2}>
                      <View style={styles.itemNameContainer}>
                        <Text style={styles.itemName}>
                          {getDisplayItemName(item)}
                        </Text>
                        {item.issueDescription && (
                          <Text style={styles.chargeDetails}>
                            {item.issueDescription}
                          </Text>
                        )}
                        <View style={{ paddingLeft: 0 }}>
                          {Array.isArray(item.charges) &&
                            item.charges.map((charge, chargeIndex) => {
                              const cleanChargeType =
                                getChargeTypeLabel(charge);
                              return (
                                <View
                                  key={chargeIndex}
                                  style={styles.chargeDetails}
                                >
                                  <Text
                                    style={{
                                      // Bold like item name, but smaller size (5.5 vs 8)
                                      fontSize: 5.5,
                                      fontWeight: "bold",
                                      color: "#000000",
                                    }}
                                  >
                                    {cleanChargeType}
                                  </Text>
                                </View>
                              );
                            })}
                        </View>
                        {item.huid && String(item.huid).trim() && (
                          <View
                            style={[
                              styles.chargeDetails,
                              { flexDirection: "row", marginTop: 2 },
                            ]}
                          >
                            <Text
                              style={{
                                fontSize: 7,
                                fontWeight: "bold",
                                color: "#000000",
                              }}
                            >
                              HUID:{" "}
                            </Text>
                            <Text
                              style={{
                                fontSize: 7,
                                fontWeight: "normal",
                                color: "#000000",
                              }}
                            >
                              {item.huid}
                            </Text>
                          </View>
                        )}
                      </View>
                      <Text style={styles.purityText}>
                        {(() => {
                          const carat = parseFloat(item?.carat);
                          return Number.isFinite(carat) && carat > 0
                            ? `${parseInt(carat, 10)}c`
                            : "";
                        })()}
                      </Text>
                      {/* Pcs / Ct under purity column (aligned with charge lines) */}
                      {Array.isArray(item.charges) &&
                        item.charges.map((charge, chargeIndex) => (
                          <View
                            key={`pcs-${chargeIndex}`}
                            style={{
                              position: "absolute",
                              right: 4,
                              top: getChargeLineTop(item, chargeIndex),
                              fontSize: 6,
                              fontWeight: "bold",
                            }}
                          >
                            <Text>
                              {charge.ct && parseFloat(charge.ct) > 0
                                ? `${parseInt(parseFloat(charge.ct) || 0, 10)}`
                                : charge.pcs && parseFloat(charge.pcs) > 0
                                  ? `${charge.pcs}`
                                  : ""}
                            </Text>
                          </View>
                        ))}
                    </View>
                    <Text style={[styles.tableCol4, styles.cellText]}>
                      {item.hsn || ""}
                    </Text>
                    <View
                      style={[
                        styles.tableCol5,
                        {
                          position: "relative",
                        },
                      ]}
                    >
                      <Text style={styles.cellText}>
                        {item.grossWeight || "0.000"}
                      </Text>
                      {Array.isArray(item.charges) &&
                        item.charges.map((charge, chargeIndex) => (
                          <View
                            key={`wt-${chargeIndex}`}
                            style={{
                              position: "absolute",
                              left: 0,
                              right: 0,
                              top: getChargeLineTop(item, chargeIndex),
                              alignItems: "center",
                            }}
                          >
                            <Text
                              style={{
                                fontSize: 6,
                                lineHeight: 1.2,
                                textAlign: "center",
                                color: "#000000",
                              }}
                            >
                              {charge.wt && parseFloat(charge.wt) > 0
                                ? `${parseFloat(charge.wt).toFixed(2)}`
                                : ""}
                            </Text>
                          </View>
                        ))}
                    </View>
                    <Text style={[styles.tableCol6, styles.cellText]}>
                      {item.netWeight || "0.000"}
                    </Text>
                    <View
                      style={[
                        styles.tableCol7,
                        {
                          position: "relative",
                        },
                      ]}
                    >
                      {(() => {
                        const rateText = `${parseFloat(item.rate || 0).toFixed(0)} + L.C.`;
                        return (
                          <Text
                            wrap={false}
                            style={[
                              styles.cellText,
                              styles.rateCellText,
                              getA5RateStyle(rateText),
                            ]}
                          >
                            {rateText}
                          </Text>
                        );
                      })()}
                      {Array.isArray(item.charges) &&
                        item.charges.map((charge, chargeIndex) => {
                          const rateOn = String(
                            charge.rate_on || charge.rateOn || "",
                          ).toLowerCase();
                          let rateText = "";
                          if (charge.rate && parseFloat(charge.rate) > 0) {
                            const r = parseFloat(charge.rate).toFixed(0);
                            if (rateOn === "ct") rateText = `${r}ct`;
                            else if (rateOn === "wt") rateText = `${r}wt`;
                            else rateText = r;
                          }
                          return (
                            <View
                              key={`rate-${chargeIndex}`}
                              style={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                top: getChargeLineTop(item, chargeIndex),
                                alignItems: "center",
                              }}
                            >
                              <Text
                                wrap={false}
                                style={{
                                  fontSize: 6,
                                  lineHeight: 1.2,
                                  textAlign: "center",
                                  color: "#000000",
                                }}
                              >
                                {rateText}
                              </Text>
                            </View>
                          );
                        })}
                    </View>
                    <View
                      style={[
                        styles.tableCol8,
                        {
                          position: "relative",
                        },
                      ]}
                    >
                      <Text style={styles.cellText}>
                        {parseFloat(item.othamt || 0).toFixed(2)}
                      </Text>
                      {Array.isArray(item.charges) &&
                        item.charges.map((charge, chargeIndex) => (
                          <View
                            key={`amt-${chargeIndex}`}
                            style={{
                              position: "absolute",
                              left: 0,
                              right: 0,
                              top: getChargeLineTop(item, chargeIndex),
                              alignItems: "center",
                            }}
                          >
                            <Text
                              style={{
                                fontSize: 6,
                                lineHeight: 1.2,
                                textAlign: "center",
                                color: "#000000",
                              }}
                            >
                              {charge.amount && parseFloat(charge.amount) > 0
                                ? `${parseFloat(charge.amount).toFixed(0)}`
                                : ""}
                            </Text>
                          </View>
                        ))}
                    </View>
                    <Text style={[styles.tableCol9, styles.amountText]}>
                      {parseFloat(item.amount || 0).toFixed(2)}
                    </Text>
                  </View>
                ))}

                {/* Trailing blank row: maintains table height and column dividers so footer stays fixed */}
                {trailingBlankRowHeight > 0 && (
                  <View
                    style={[
                      styles.emptyRow,
                      { minHeight: trailingBlankRowHeight },
                    ]}
                  >
                    <View style={[styles.tableCol1, styles.fillerCol]} />
                    <View style={[styles.tableCol2, styles.fillerCol]} />
                    <View style={[styles.tableCol4, styles.fillerCol]} />
                    <View style={[styles.tableCol5, styles.fillerCol]} />
                    <View style={[styles.tableCol6, styles.fillerCol]} />
                    <View style={[styles.tableCol7, styles.fillerCol]} />
                    <View style={[styles.tableCol8, styles.fillerCol]} />
                    <View style={[styles.tableCol9, styles.fillerCol]} />
                  </View>
                )}

                <View
                  style={{
                    borderTop: BORDER_LINE,
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  <View style={styles.footerSummaryBody}>
                    <View style={styles.footerVLine} />
                    <View style={styles.totalRow}>
                      <View style={styles.summaryTotalLeft}>
                        <Text style={styles.noteLabel}>NOTE:</Text>
                        <Text style={styles.noteValue}>{data.note || ""}</Text>
                      </View>
                      <View style={styles.summaryTotalRight}>
                        <Text style={styles.totalAmountLabel}>
                          TOTAL AMOUNT
                        </Text>
                        <Text style={styles.totalAmountValue}>
                          {totalBeforeTax.toFixed(2)}
                        </Text>
                      </View>
                    </View>

                    <View style={styles.summaryGrid}>
                      <View style={styles.summaryRow1}>
                        <View style={styles.amountWordsCell}>
                          <Text>{data.amountInWords}</Text>
                        </View>
                        <View style={styles.taxRowCell}>
                          {discountAmountForRound > 0 ? (
                            <>
                              <Text style={styles.taxLabel}>DISCOUNT</Text>
                              <Text style={styles.taxValue}>
                                {discountAmountForRound.toFixed(2)}
                              </Text>
                            </>
                          ) : (
                            <>
                              <Text style={styles.taxLabel}>
                                SGST (
                                {displaySgstAmount > 0
                                  ? (gstPercent / 2).toFixed(2)
                                  : "0"}
                                %)
                              </Text>
                              <Text style={styles.taxValue}>
                                {displaySgstAmount.toFixed(2)}
                              </Text>
                            </>
                          )}
                        </View>
                      </View>

                      <View style={styles.summaryRowBody}>
                        <View style={styles.summaryColLeft}>
                          <View style={styles.paymentSplitRow}>
                            <View style={styles.paymentVLine} />
                            <View style={styles.paymentSplitLeft}>
                              <View style={styles.paymentPad}>
                                {leftColumnPayments.map((payment, index) => {
                                  const isOldMetal =
                                    isOldMetalPayment(payment);
                                  return (
                                    <View
                                      key={`pay-${index}`}
                                      style={[
                                        styles.paymentMethod,
                                        { marginTop: index === 0 ? 2 : 1 },
                                      ]}
                                    >
                                      <Text style={styles.paymentMethodLabel}>
                                        {isOldMetal
                                          ? getOldMetalPaymentLabel(payment)
                                          : getPaymentLabel(payment)}{" "}
                                        :
                                      </Text>
                                      <Text style={{ fontSize: 7 }}>
                                        {" "}
                                        {getPaymentAmount(payment).toFixed(2)}
                                      </Text>
                                    </View>
                                  );
                                })}
                              </View>
                            </View>
                            <View style={styles.paymentSplitRight}>
                              <View style={styles.paymentPad}>
                                {cashReturnAmount > 0 && (
                                  <View
                                    style={[
                                      styles.paymentMethod,
                                      { marginTop: 2 },
                                    ]}
                                  >
                                    <Text style={styles.paymentMethodLabel}>
                                      Cash Return :
                                    </Text>
                                    <Text style={{ fontSize: 7 }}>
                                      {" "}
                                      {cashReturnAmount.toFixed(2)}
                                    </Text>
                                  </View>
                                )}
                              </View>
                            </View>
                          </View>
                        </View>

                        <View style={styles.summaryColRight}>
                          {discountAmountForRound > 0 && (
                            <View style={styles.taxRow}>
                              <Text style={styles.taxLabel}>
                                SGST (
                                {displaySgstAmount > 0
                                  ? (gstPercent / 2).toFixed(2)
                                  : "0"}
                                %)
                              </Text>
                              <Text style={styles.taxValue}>
                                {displaySgstAmount.toFixed(2)}
                              </Text>
                            </View>
                          )}
                          <View style={styles.taxRow}>
                            <Text style={styles.taxLabel}>
                              CGST (
                              {displayCgstAmount > 0
                                ? (gstPercent / 2).toFixed(2)
                                : "0"}
                              %)
                            </Text>
                            <Text style={styles.taxValue}>
                              {displayCgstAmount.toFixed(2)}
                            </Text>
                          </View>
                          <View style={styles.taxRow}>
                            <Text style={styles.taxLabel}>
                              IGST (
                              {displayIgstAmount > 0
                                ? gstPercent.toFixed(2)
                                : "0"}
                              %)
                            </Text>
                            <Text style={styles.taxValue}>
                              {displayIgstAmount.toFixed(2)}
                            </Text>
                          </View>
                          <View style={styles.taxRow}>
                            <Text style={styles.taxLabel}>{roundOffLabel}</Text>
                            <Text style={styles.taxValue}>
                              {autoRoundOffAmount.toFixed(2)}
                            </Text>
                          </View>
                          <View style={styles.taxRow}>
                            <Text style={styles.taxLabel}>
                              TOTAL AMOUNT AFTER TAX
                            </Text>
                            <Text style={styles.taxValue}>
                              {amountAfterTax.toFixed(2)}
                            </Text>
                          </View>
                          <View style={styles.taxRow}>
                            <Text style={styles.taxLabel}>ADVANCE PAYMENT</Text>
                            <Text style={styles.taxValue}>
                              {data.advancePayment || "0.00"}
                            </Text>
                          </View>
                          <View style={styles.taxRow}>
                            <Text style={styles.taxLabel}>TOTAL PAID</Text>
                            <Text style={styles.taxValue}>
                              {data.paidAmount || "0.00"}
                            </Text>
                          </View>
                          <View style={[styles.taxRow, { borderBottom: 0 }]}>
                            <Text style={styles.taxLabel}>
                              FINAL OUTSTANDING
                            </Text>
                            <Text style={styles.taxValue}>
                              {data.remainingAmount || "0.00"}
                            </Text>
                          </View>
                        </View>
                      </View>
                    </View>
                  </View>

                  <View style={styles.authSection}>
                    <View style={styles.footerTopRow}>
                      <Text style={styles.jurisdictionText}>
                        SUBJECT TO AHMEDABAD JURISDICTION.
                      </Text>
                      <View style={styles.footerTopRight}>
                        <Text style={styles.certificationText}>
                          Certified that the particulars given above are true
                          and correct.
                        </Text>
                      </View>
                    </View>

                    <View style={styles.signatureSection}>
                      <View style={styles.signatureColumnLeft}>
                        <Text style={{ height: 20 }}></Text>
                        <Text style={styles.signatureText}>
                          CUSTOMER SIGNATURE
                        </Text>
                      </View>
                      <View style={styles.signatureColumnCenter} />
                      <View style={styles.signatureColumnRight}>
                        <View style={styles.signatureColumnRightTop}>
                          <Text style={styles.companySignature}>
                            For, {companyNameUpper}
                          </Text>
                          <Image
                            src={getTenantBrandImageSrcWithFallback("signature")}
                            style={styles.signatureImage}
                          />
                        </View>
                        <Text style={styles.signatureText}>
                          AUTHORISED SIGNATURE
                        </Text>
                      </View>
                    </View>
                  </View>
                </View>
              </View>
            </View>
          </Page>
        );
      })}
    </Document>
  );
};

export default InvoicePDFA5;
